import React, { useCallback, useEffect, useRef } from 'react';
import { ApplicationView, observable, raw } from '@aha-app/mvc';
import { type EditorPlugin } from '../../types/aha-editor';
import { getWordAndCharacterCount } from './utils/getWordAndCharacterCount';
import WordCount from './components/WordCount';
import type AhaEditorV2Controller from '../../controllers/AhaEditorV2Controller';
import { type Editor, Range } from 'slate';

interface WordCountPluginState {
  modalElement: HTMLElement | null;
  modalOpen: boolean;
  document: {
    words: number;
    characters: number;
  };
  selection: {
    words: number;
    characters: number;
  } | null;
}

export type WordCountPluginType = EditorPlugin<{
  openModal(): void;
}>;

export const WORD_COUNT_PLUGIN_ID = 'word-count-plugin';

const getDocumentAndSelectionCount = (editor: Editor) => {
  const document = getWordAndCharacterCount(editor);
  const selection =
    editor.selection && Range.isExpanded(editor.selection)
      ? getWordAndCharacterCount(editor, editor.selection)
      : null;
  return { document, selection };
};

const WordCountPlugin = (): WordCountPluginType => {
  const state: WordCountPluginState = observable({
    modalElement: null,
    modalOpen: false,
    document: {
      words: 0,
      characters: 0,
    },
    selection: {
      words: 0,
      characters: 0,
    },
  });

  let controller: AhaEditorV2Controller;

  const WordCountComponent = () => {
    const modal = useRef<HTMLElement>(null);

    const closeModal = useCallback(() => {
      if (!state.modalElement) return;

      const modal = raw(state.modalElement);
      // @ts-expect-error hide() exists on HTMLAhaModalElement
      modal.hide();
    }, []);

    useEffect(() => {
      if (!modal.current) return;

      state.modalElement = modal.current;

      const onClose = () => {
        state.modalOpen = false;
      };

      modal.current.addEventListener('aha-modal:close', onClose);

      return () => {
        if (modal.current) {
          modal.current.removeEventListener('aha-modal:close', onClose);
        }
      };
    }, []);

    return (
      <div className="word-count-plugin-modal">
        <aha-modal ref={modal}>
          <aha-modal-header>Word count</aha-modal-header>
          <aha-modal-body>
            <WordCount document={state.document} selection={state.selection} />
          </aha-modal-body>
          <aha-modal-footer>
            <aha-button onClick={closeModal}>Close</aha-button>
          </aha-modal-footer>
        </aha-modal>
      </div>
    );
  };

  return {
    id: WORD_COUNT_PLUGIN_ID,
    name: 'Word count plugin',
    layoutComponent: {
      Component: ApplicationView(WordCountComponent),
      location: 'below',
    },
    handlers: {
      onChange: (ahaEditor) => {
        if (!state.modalOpen) return;

        const { document, selection } = getDocumentAndSelectionCount(
          ahaEditor.editor
        );
        state.document = document;
        state.selection = selection;
      },
      onInit: (ahaEditor) => {
        controller = ahaEditor.controller;
      },
    },
    api: {
      openModal: () => {
        if (!state.modalElement) return;

        const { document, selection } = getDocumentAndSelectionCount(
          controller.editor
        );
        state.document = document;
        state.selection = selection;

        state.modalOpen = true;
        const modal = raw(state.modalElement);

        // @ts-expect-error show() exists on HTMLAhaModalElement
        modal.show();
      },
    },
  };
};

export default WordCountPlugin;
